import { KnownMessageTypes } from "@features/Chat/components/Message/helpers";
import { persist, createJSONStorage } from "zustand/middleware";
import { sortByCreatedAt } from "@shared/utils/sortBy";
import { create } from "zustand";
import { MessageArtifactsInner } from "@shared/apis/sidekick/generated";

export type ChatMessageArtifact = MessageArtifactsInner;

export type ChatMessage = {
  id: string;
  text: string;
  createdAt: Date;
  streaming?: boolean;
  subMessages?: Omit<ChatMessage, "subMessages">[];
  artifacts?: ChatMessageArtifact[];
  type: KnownMessageTypes;
  user: {
    name: string;
    avatar: string;
  };
  createdBy: string;
};

export type ChatMessageMetadata = {
  messageId: string;
  elapsedTime: number;
};

type UpsertChatMessageType = Partial<ChatMessage> & Pick<ChatMessage, "id">;

interface MessageState {
  messagesMap: Map<string, ChatMessage[]>;
  messagesMetadataMap: Map<string, ChatMessageMetadata>;
  promptRefId: string | null;
  /**
   * True from the moment a brand-new thread's first message starts sending
   * until it's actually sent (or fails) — survives the route remount that
   * happens once the thread is created and the URL changes to /chat/:id,
   * unlike a mutation's own `isPending` (a fresh observer on the new page).
   */
  isCreatingThread: boolean;
  upsertMessage: (
    threadId: string,
    message: UpsertChatMessageType,
    metadata?: { elapsedTime: number },
  ) => void;
  addSubMessage: (
    threadId: string,
    messageId: string,
    subMessage: Omit<ChatMessage, "subMessages">,
  ) => void;
  setMessages: (threadId: string, messages: ChatMessage[]) => void;
  movePendingThreadMessages: (threadId: string) => void;
  updateStreamMessageText: (
    threadId: string,
    tempMessageId: string,
    text: string,
    isSubMessage?: boolean,
  ) => void;
  syncStreamMessage: (
    threadId: string,
    tempMessageId: string,
    message: ChatMessage,
    metadata?: { elapsedTime: number },
  ) => void;
  setPromptRefId: (promptRefId: string | null) => void;
  setIsCreatingThread: (isCreatingThread: boolean) => void;
}

function mergeTexts(existingText: string, newText: string): string {
  return [existingText, newText]
    .join("")
    .replace(/([^\n])\n\*\*/g, "$1\n\n**") // in .md we need at least 2 \n to ensure we "got to next line"
    .replace(/\.\n\*\*/g, ".\n\n**"); // in .md we need at least 2 \n to ensure we "got to next line"
}

function mergeSameIdMessages(messages: UpsertChatMessageType[]): ChatMessage[] {
  return Object.values(
    messages.reduce<Record<string, ChatMessage>>((acc, msg) => {
      acc[msg.id] = {
        ...(acc[msg.id] || {}),
        ...msg,
        text:
          acc[msg.id]?.text !== msg.text
            ? mergeTexts(acc[msg.id]?.text, msg.text || "")
            : msg.text || "",
      };
      return acc;
    }, {}),
  );
}

function parseAndEnhanceMessages(messages: ChatMessage[]) {
  const deduplicatedMessages = mergeSameIdMessages(messages);
  const messagesWithSubmessages = Object.values(
    deduplicatedMessages.reduce<Record<string, ChatMessage>>((acc, msg) => {
      const msgMatches = msg.id.match(
        /(resp_[a-f0-9]+)(?:(?:_)((?:rs|fc)_[a-f0-9]+))?/i,
      );
      const mainMsgId = msgMatches?.[1] || "";
      const subMessageId = msgMatches?.[2] || "";
      const currentSubMessages = acc[mainMsgId]?.subMessages || [];

      if (subMessageId) {
        acc[mainMsgId] = {
          ...(acc[mainMsgId] || {}),
          id: mainMsgId,
          subMessages: [...currentSubMessages, msg],
        };
        return acc;
      }
      const hasSubMessages = !!currentSubMessages.length;
      const mainMsgWithSubmessages: ChatMessage = hasSubMessages
        ? { ...msg, subMessages: [...currentSubMessages] }
        : msg;
      if (hasSubMessages) delete acc[mainMsgId];
      if (msg.id) acc[msg.id] = mainMsgWithSubmessages;
      return acc;
    }, {}),
  );
  return messagesWithSubmessages;
}

export const TEMP_USER_MESSAGE_PREFIX = "temp-";
export const TEMP_REASONING_MESSAGE_ID = "rs_temp-reasoning-id";
/**
 * Holds the optimistic user message for a thread that doesn't exist yet —
 * written the instant the first message of a new chat is sent, moved to the
 * real thread id via `movePendingThreadMessages` once it's created.
 */
export const PENDING_THREAD_KEY = "__pending_thread__";
const MESSAGES_STORAGE_ID = "@sidekick:messages-storage";
export const buildMessageMetadataKey = (
  threadId: string,
  messageId: string,
) => {
  return `${threadId}:${messageId.split("000Z")[0]}`;
};

export const useMessagesStore = create<MessageState>()(
  persist(
    (set) => ({
      messagesMap: new Map(),
      messagesMetadataMap: new Map(),
      promptRefId: null,
      isCreatingThread: false,
      upsertMessage: (
        threadId: string,
        message: UpsertChatMessageType,
        metadata,
      ) =>
        set((state) => {
          const existingMessages = state.messagesMap.get(threadId) || [];
          const updatedMessages = mergeSameIdMessages([
            ...existingMessages,
            message,
          ]);

          return {
            ...state,
            messagesMetadataMap: metadata
              ? new Map(state.messagesMetadataMap).set(
                  buildMessageMetadataKey(threadId, message.id),
                  { messageId: message.id, ...metadata },
                )
              : state.messagesMetadataMap,
            messagesMap: new Map(state.messagesMap).set(
              threadId,
              updatedMessages,
            ),
          };
        }),
      addSubMessage: (threadId, messageId, subMessage) =>
        set((state) => {
          const threadMessages = state.messagesMap.get(threadId) || [];
          const mainMessage = threadMessages.find((m) => m.id === messageId);
          if (!mainMessage) {
            console.warn(
              `Message with id ${messageId} not found in thread ${threadId}`,
            );
            return state;
          }
          let subMessages = mainMessage.subMessages || [];
          const isReasoningMessage =
            subMessage.id.startsWith("rs_") || subMessage.type === "reasoning";
          if (isReasoningMessage && !subMessage.streaming) {
            // if a complete reasoning message is arriving replace the temp
            const fixedTextMessage = {
              ...subMessage,
              text: mergeTexts(subMessage.text, ""),
            };
            subMessages = subMessages.map((m) =>
              m.id === TEMP_REASONING_MESSAGE_ID ? fixedTextMessage : m,
            );
          } else {
            subMessages = mergeSameIdMessages([
              ...(subMessages || []),
              subMessage,
            ]);
          }

          const messageWithSubMessages = {
            ...mainMessage,
            subMessages,
          } satisfies ChatMessage;
          const updatedThreadMessages = threadMessages.map((m) =>
            m.id === messageId ? messageWithSubMessages : m,
          );
          return {
            ...state,
            messagesMap: new Map(state.messagesMap).set(
              threadId,
              updatedThreadMessages,
            ),
          };
        }),
      setMessages: (threadId: string, messages: ChatMessage[]) =>
        set((state) => {
          if (messages.some((m) => m.text?.trim() === "")) {
            return state;
          }

          const messagesMap = new Map(state.messagesMap);
          const debugMessages = (messagesMap.get(threadId) || []).filter(
            (m) => m.type === "debug-json",
          );

          const enhancedMessages = parseAndEnhanceMessages([
            ...messages,
            ...debugMessages,
          ]);

          return {
            ...state,
            messagesMap: new Map(state.messagesMap).set(
              threadId,
              sortByCreatedAt(enhancedMessages),
            ),
          };
        }),
      movePendingThreadMessages: (threadId) =>
        set((state) => {
          const pending = state.messagesMap.get(PENDING_THREAD_KEY);
          if (!pending?.length) return state;

          const existing = state.messagesMap.get(threadId) || [];
          const merged = mergeSameIdMessages([...existing, ...pending]);

          // Deliberately not deleting PENDING_THREAD_KEY here — with
          // react-router's v7_startTransition, this store update commits in
          // its own paint before the navigate() transition lands, so a
          // still-empty currentThread would briefly have nowhere to read the
          // message from if it were cleared now. It's overwritten fresh next
          // time a thread-less message is sent (see MessageBar).
          const messagesMap = new Map(state.messagesMap);
          messagesMap.set(threadId, sortByCreatedAt(merged));

          return { ...state, messagesMap };
        }),
      updateStreamMessageText: (threadId, tempMessageId, text, isSubMessage) =>
        set((state) => {
          const messages = state.messagesMap.get(threadId);

          if (!messages) return state;

          const newMessages: ChatMessage[] = messages.map((message) => {
            if (message.id !== tempMessageId) return message;
            if (!isSubMessage || message.subMessages?.length) {
              return {
                ...message,
                text: mergeTexts(message.text, text),
              };
            }
            const lastSubMessage = message.subMessages?.at(-1);
            if (!lastSubMessage) return { ...message };
            const updatedMessage = {
              ...lastSubMessage,
              text: mergeTexts(lastSubMessage.text, text),
            };
            return {
              ...message,
              subMessages: mergeSameIdMessages([
                ...(message.subMessages || []),
                updatedMessage,
              ]),
            };
          });

          return {
            ...state,
            messagesMap: new Map(state.messagesMap).set(threadId, newMessages),
          };
        }),
      syncStreamMessage: (threadId, tempMessageId, message, metadata) =>
        set((state) => {
          const messages = state.messagesMap.get(threadId);

          if (!messages) return state;

          const newMessages: ChatMessage[] = messages.map((m) => {
            if (tempMessageId === m.id) {
              return { ...m, ...message };
            }
            return m;
          });

          return {
            ...state,
            messagesMetadataMap: metadata
              ? new Map(state.messagesMetadataMap).set(
                  buildMessageMetadataKey(threadId, message.id),
                  { messageId: message.id, ...metadata },
                )
              : state.messagesMetadataMap,
            messagesMap: new Map(state.messagesMap).set(
              threadId,
              sortByCreatedAt(newMessages, (x, y) => x.id.localeCompare(y.id)),
            ),
          };
        }),
      setPromptRefId: (promptRefId) =>
        set((state) => {
          return {
            ...state,
            promptRefId,
          };
        }),
      setIsCreatingThread: (isCreatingThread) =>
        set((state) => ({
          ...state,
          isCreatingThread,
        })),
    }),
    {
      name: MESSAGES_STORAGE_ID,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        messagesMap: Object.fromEntries(state.messagesMap),
      }),
      merge: (store, incoming) => {
        if (!store) {
          return incoming;
        }

        try {
          const persistedData = store as {
            messagesMap?: Record<string, ChatMessage[]>;
          };

          if (
            !persistedData.messagesMap ||
            Object.keys(persistedData.messagesMap).length === 0
          ) {
            return incoming;
          }

          const restoredMessagesMap = new Map(
            Object.entries(persistedData.messagesMap),
          );

          return {
            ...incoming,
            messagesMap: restoredMessagesMap,
          };
        } catch (error) {
          console.error(
            "Failed to restore persisted messages, using current state:",
            error,
          );
          return incoming;
        }
      },
    },
  ),
);
